import React, { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import XanoAPI from "../../../lib/XanoAPI";
import { useToast } from "../../../components/ui/use-toast";
import { ClientType } from "../../../types";
// Form components
import PersonalInformationComponent from "../components/personal-information";
import FamilyInformationComponent from "../components/family-information";
import RetirementInformationComponent from "../components/retirement-information";
import SavingsInformationComponent from "../components/savings-information";
import ContactComponent from "../components/contact-component";
// UI components
import { Icons } from "../../../utils/icons";
import { Button } from "../../../components/ui/button";
import { useAuth } from "../../../lib/AuthContext";
import type { AutoSaveStatus } from "../../../hooks/useIntervalAutoSave";
import { Clock, Sparkles, Info } from "lucide-react";
// SmartPasteStepModal is now imported in parent (edit-forms.tsx)
import { Alert, AlertDescription } from "../../ui/alert";
import { getStates } from "../../../lib/utils";

// Initialize Xano API instance
const api = XanoAPI.getInstance();

// **Updated Zod Schema**
const formSchema = z.object({
  user_id: z.number(),
  firstName: z.string().min(2, "First Name must be at least 2 characters."),
  lastName: z.string().min(2, "Last Name must be at least 2 characters."),
  gender: z.enum(["male", "female"]).optional(),
  phoneNumber: z.string().min(10, "Phone number must be at least 10 digits."),
  mobilePhone: z.string().optional(),
  // Email is optional - empty string or valid email
  email: z.string().optional().or(z.string().email("Invalid email address.")),
  stateProvince: z
    .string()
    .min(2, "State/Province must be at least 2 characters."),
  type: z.enum(["federal", "401k", "403b"]),
  status: z.enum(["Active", "Inactive", "Pending"]),
  dateOfBirth: z.string().optional(),
  spousesName: z.string().optional(),
  spousesDateOfBirth: z.string().nullable().optional(),
  related_client_id: z.number().nullable().optional(), // Link to spouse client
  dependents: z.number().optional(), // Added missing field
  targetRetirementAge: z.number().optional(),
  targetRetirementMonth: z.string().optional(),
  lifeExpectancyAge: z.number().optional(),
  interestRatePriorToRetirement: z.number().optional(),
  interestRateInRetirement: z.number().optional(),
  annuallyIncreaseSavingsWithRaises: z.boolean().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  // zipCode can be number or string in DB, coerce to string
  zipCode: z
    .union([z.string(), z.number()])
    .optional()
    .transform((val) => val?.toString() ?? ""),
  // **CRM Fields**
  federalAgency: z.string().optional(),
  lastContactDate: z.string().optional(),
  preferredContactMethod: z.string().optional(),
  contactSource: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

export default function PersonalInformation({
  client,
  onUpdate,
  handleTabChange,
  setFormsDirty,
  fetchClientData,
  resetFormsDirtyState,
  formsDirty,
  saveStatus,
  lastSaveTime,
  isSmartPasteOpen,
  setIsSmartPasteOpen,
  setSmartPasteHandlers,
}: {
  client: ClientType;
  onUpdate: Function;
  handleTabChange?: Function;
  setFormsDirty: Function;
  fetchClientData: Function;
  resetFormsDirtyState: Function;
  formsDirty: any;
  saveStatus: AutoSaveStatus;
  lastSaveTime: number;
  isSmartPasteOpen: boolean;
  setIsSmartPasteOpen: (open: boolean) => void;
  setSmartPasteHandlers: (handlers: any) => void;
}) {
  const { toast } = useToast();
  const isAutoSaving = React.useRef(false);
  const auth = useAuth();
  const [crmEnabled, setCrmEnabled] = useState<boolean>(
    !!auth?.currentUser?.module_crm_plan
  );
  
  // Smart Paste modal state (managed by parent, but local handlers)
  const [pendingFinancialData, setPendingFinancialData] = useState<Partial<ClientType> | null>(null);
  const [showFinancialPrompt, setShowFinancialPrompt] = useState(false);
  
  // Smart Paste operation flag - prevents form reset during population
  const isSmartPastePopulating = React.useRef(false);
  
  // Refs for handler functions to avoid stale closures
  const handlersRef = React.useRef({
    onPersonalDataParsed: (data: Partial<ClientType>) => {
      console.log('⚠️ onPersonalDataParsed called but not initialized yet');
    },
    onFinancialDataParsed: (data: Partial<ClientType>) => {
      console.log('⚠️ onFinancialDataParsed called but not initialized yet');
    },
    onPopulateSingleField: (fieldName: string, value: any) => {
      console.log('⚠️ onPopulateSingleField called but not initialized yet');
    },
    onSave: async () => {
      console.log('⚠️ onSave called but not initialized yet');
    },
  });
  
  // Real-time auto-save text with live updates
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Initialize react-hook-form with defaultValues that update when client changes
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange", // Enable real-time dirty tracking
    criteriaMode: "all", // Report all errors
    reValidateMode: "onChange", // Revalidate on every change
    defaultValues: {
      user_id: api.getUserId(),
      firstName: client?.firstName ?? "",
      lastName: client?.lastName ?? "",
      gender: client?.gender ?? "male",
      phoneNumber: client?.phoneNumber ?? "",
      mobilePhone: client?.mobilePhone ?? "",
      email: client?.email ?? "",
      stateProvince: client?.stateProvince ?? "",
      type: client?.type ?? "federal",
      status: client?.status ?? "Active",
      dateOfBirth: client?.dateOfBirth ?? "",
      spousesName: client?.spousesName ?? "",
      spousesDateOfBirth: client?.spousesDateOfBirth ?? "",
      related_client_id: client?.related_client_id ?? null,
      dependents: client?.dependents ?? 0,
      targetRetirementAge: client?.targetRetirementAge ?? 0,
      targetRetirementMonth: client?.targetRetirementMonth ?? "",
      lifeExpectancyAge: client?.lifeExpectancyAge ?? 0,
      interestRatePriorToRetirement: client?.interestRatePriorToRetirement ?? 0,
      interestRateInRetirement: client?.interestRateInRetirement ?? 0,
      annuallyIncreaseSavingsWithRaises:
        client?.annuallyIncreaseSavingsWithRaises ?? false,
      address: client?.address ?? "",
      city: client?.city ?? "",
      zipCode: client?.zipCode ?? "",
      federalAgency: client?.federalAgency ?? "",
      lastContactDate: client?.lastContactDate ?? "",
      preferredContactMethod: client?.preferredContactMethod ?? "email",
      contactSource: client?.contactSource ?? "referral",
    },
  });

  const { handleSubmit, watch, setValue, reset, control, formState, trigger, getValues } =
    form;

  // Access formState properties directly (not destructured) for reactivity
  const { isValid, isDirty, dirtyFields } = formState;
  
  // Register Smart Paste handlers with parent on mount/update (after form is initialized)
  React.useEffect(() => {
    if (setSmartPasteHandlers) {
      console.log('📝 [Personal Tab] Registering Smart Paste handlers');
      setSmartPasteHandlers((prev: any) => ({
        ...prev,
        personal: {
          onPersonalDataParsed: (data: any) => {
            console.log('📞 [Personal Tab] onPersonalDataParsed wrapper called');
            return handlersRef.current.onPersonalDataParsed(data);
          },
          onFinancialDataParsed: (data: any) => {
            console.log('📞 [Personal Tab] onFinancialDataParsed wrapper called');
            return handlersRef.current.onFinancialDataParsed(data);
          },
          onPopulateSingleField: (fieldName: string, value: any) => {
            console.log('📞 [Personal Tab] onPopulateSingleField wrapper called');
            return handlersRef.current.onPopulateSingleField(fieldName, value);
          },
          onSave: () => {
            console.log('📞 [Personal Tab] onSave wrapper called');
            return handlersRef.current.onSave();
          },
          isSaveDisabled: !isValid || !isDirty,
        },
      }));
      console.log('✅ [Personal Tab] Handlers registered successfully');
    }
  }, [setSmartPasteHandlers, client?.id, isValid, isDirty]); // Include isValid/isDirty to update isSaveDisabled

  // Track form state changes for debugging
  // Form state tracking (commented out after debugging)
  // useEffect(() => {
  //   console.log("Form state:", { isDirty, isValid, dirtyFieldCount: Object.keys(dirtyFields || {}).length });
  // }, [isDirty, isValid, dirtyFields]);

  // Reset form when client changes (to update dirty tracking baseline)
  useEffect(() => {
    // GUARD: Don't reset during Smart Paste operations!
    if (isSmartPastePopulating.current) {
      return;
    }
    
    if (client?.id) {
      reset({
        user_id: api.getUserId(),
        firstName: client.firstName ?? "",
        lastName: client.lastName ?? "",
        gender: client.gender ?? "male",
        phoneNumber: client.phoneNumber ?? "",
        mobilePhone: client.mobilePhone ?? "",
        email: client.email ?? "",
        stateProvince: client.stateProvince ?? "",
        type: client.type ?? "federal",
        status: client?.status ?? "Active",
        dateOfBirth: client.dateOfBirth ?? "",
        spousesName: client.spousesName ?? "",
        spousesDateOfBirth: client.spousesDateOfBirth ?? "",
        related_client_id: client.related_client_id ?? null,
        dependents: client.dependents ?? 0,
        targetRetirementAge: client.targetRetirementAge ?? 0,
        targetRetirementMonth: client.targetRetirementMonth ?? "",
        lifeExpectancyAge: client.lifeExpectancyAge ?? 0,
        interestRatePriorToRetirement:
          client.interestRatePriorToRetirement ?? 0,
        interestRateInRetirement: client.interestRateInRetirement ?? 0,
        annuallyIncreaseSavingsWithRaises:
          client.annuallyIncreaseSavingsWithRaises ?? false,
        address: client.address ?? "",
        city: client.city ?? "",
        zipCode: client.zipCode ?? "",
        federalAgency: client.federalAgency ?? "",
        lastContactDate: client.lastContactDate ?? "",
        preferredContactMethod: client.preferredContactMethod ?? "email",
        contactSource: client.contactSource ?? "referral",
      });
      // Trigger validation after reset to sync isValid state
      setTimeout(() => trigger(), 0);
    }
  }, [client?.id, client?.related_client_id, reset, trigger]);

  const watchedCity = watch("city");
  const watchedState = watch("stateProvince");
  const watchedZip = watch("zipCode");

  // Sync React Hook Form's isDirty state with parent's formsDirty state
  useEffect(() => {
    if (isDirty && dirtyFields) {
      const dirtyFieldNames = Object.keys(dirtyFields);
      setFormsDirty((prev: any) => ({
        ...prev,
        personalInformation: dirtyFieldNames,
      }));
    } else {
      // Clear dirty state when form is clean
      setFormsDirty((prev: any) => ({
        ...prev,
        personalInformation: [],
      }));
    }
  }, [isDirty, dirtyFields, isValid]);

  // Update current time every second for real-time display
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  if (!client) return null;

  const onSubmit = async (data: FormData) => {
    if (!client || !client.id) {
      console.error("Client ID is missing.");
      return;
    }

    try {
      console.log('onSubmit - data.related_client_id:', data.related_client_id);
      // Only send fields that are in the PersonalInformation form schema
      const updateData: Partial<ClientType> = {
        user_id: data.user_id,
        firstName: data.firstName,
        lastName: data.lastName,
        gender: data.gender,
        phoneNumber: data.phoneNumber,
        mobilePhone: data.mobilePhone,
        email: data.email,
        stateProvince: data.stateProvince,
        type: data.type,
        status: data.status,
        dateOfBirth: data.dateOfBirth,
        spousesName: data.spousesName,
        spousesDateOfBirth: data.spousesDateOfBirth,
        related_client_id: data.related_client_id,
        dependents: data.dependents,
        targetRetirementAge: data.targetRetirementAge,
        targetRetirementMonth: data.targetRetirementMonth,
        lifeExpectancyAge: data.lifeExpectancyAge,
        interestRatePriorToRetirement: data.interestRatePriorToRetirement,
        interestRateInRetirement: data.interestRateInRetirement,
        annuallyIncreaseSavingsWithRaises:
          data.annuallyIncreaseSavingsWithRaises,
        address: data.address,
        city: data.city,
        zipCode: data.zipCode,
        federalAgency: data.federalAgency,
        lastContactDate: data.lastContactDate,
        preferredContactMethod: data.preferredContactMethod,
        contactSource: data.contactSource,
        lifecycleStage: client.lifecycleStage,
      };

      console.log('Sending updateData to API:', updateData);
      await api.updateClient(client.id.toString(), updateData);

      // Fetch updated data immediately - this will trigger a re-render with fresh client data
      await fetchClientData(client.id);
      
      // Note: The useEffect hook will handle resetting the form when client data updates
      console.log('API update complete, waiting for client prop to update...');

      // Clear the parent's dirty state
      resetFormsDirtyState();

      // Only show toast notification on manual save, not auto-save
      if (!isAutoSaving.current) {
        toast({
          title: "Personal Details Updated!",
          description: "Your client's information has been updated.",
        });
      }
      
      // Reset the auto-save flag
      isAutoSaving.current = false;
    } catch (error) {
      console.error("Error updating client:", error);
      if (error instanceof Error && error.message.includes("401")) {
        api.signOut();
      }
    }
  };

  const getAutoSaveText = () => {
    if (lastSaveTime === 0) return null;
    
    const timeSinceLastSave = currentTime - lastSaveTime;
    const seconds = Math.floor(timeSinceLastSave / 1000);
    const minutes = Math.floor(seconds / 60);

    if (minutes > 0) {
      return `Autosaved ${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    } else if (seconds > 5) {
      return `Autosaved ${seconds} seconds ago`;
    } else if (seconds > 0) {
      return "Autosaved just now";
    }
    return null;
  };

  // Handle single field population from Smart Paste
  const handlePopulateSingleField = (fieldName: string, value: any) => {
    isSmartPastePopulating.current = true;
    
    try {
      setValue(fieldName as any, value, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
      
      // Update parent dirty state
      setFormsDirty((prev: any) => ({
        ...prev,
        personalInformation: [...(prev.personalInformation || []), fieldName],
      }));
      
      console.log(`✅ Field applied: ${fieldName} = ${value}`);
    } catch (err) {
      console.error(`Failed to apply field ${fieldName}:`, err);
    }
    
    setTimeout(() => {
      isSmartPastePopulating.current = false;
    }, 100);
  };
  
  // Assign handler to ref
  console.log('🔄 [Personal Tab] Assigning onPopulateSingleField to ref');
  handlersRef.current.onPopulateSingleField = handlePopulateSingleField;

  // Handle Smart Paste - Personal Information (Step 1)
  const handlePersonalDataParsed = (parsedData: Partial<ClientType>) => {
    console.log("🤖 Smart Paste: Populating", Object.keys(parsedData).length, "personal fields");

    // Remove fields that require specific JSON structure (ItemsComponent fields)
    const fieldsToRemove = ['deductions', 'additionalIncome', 'allocations', 'federal_tsp_allocations', 'unused_sick_leave'];
    fieldsToRemove.forEach(field => delete parsedData[field as keyof ClientType]);

    // State name to abbreviation mapping
    const stateNameToAbbr: Record<string, string> = {
      'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA',
      'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA',
      'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
      'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
      'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO',
      'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
      'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH',
      'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
      'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT',
      'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY',
      'district of columbia': 'DC', 'washington dc': 'DC', 'washington d.c.': 'DC'
    };

    // Convert state names to abbreviations and validate against getStates() options
    if (parsedData.stateProvince) {
      const stateValue = String(parsedData.stateProvince).trim();
      let validStateValue: string | undefined = undefined;
      
      if (stateValue.length === 0 || stateValue.length === 1) {
        // Invalid - too short, skip it
        validStateValue = undefined;
      } else if (stateValue.length === 2) {
        // Already an abbreviation - validate it exists in getStates()
        const upperValue = stateValue.toUpperCase();
        const stateOptions = getStates().map(s => s.value);
        if (stateOptions.includes(upperValue)) {
          validStateValue = upperValue;
        } else {
          console.warn(`⚠️ State abbreviation "${upperValue}" not found in getStates() options`);
          validStateValue = undefined;
        }
      } else {
        // Full state name - convert to abbreviation
        const stateLower = stateValue.toLowerCase();
        if (stateNameToAbbr[stateLower]) {
          const abbr = stateNameToAbbr[stateLower];
          const stateOptions = getStates().map(s => s.value);
          if (stateOptions.includes(abbr)) {
            validStateValue = abbr;
          } else {
            console.warn(`⚠️ Converted state "${abbr}" not found in getStates() options`);
            validStateValue = undefined;
          }
        } else {
          console.warn(`⚠️ State name "${stateValue}" not found in stateNameToAbbr mapping`);
          validStateValue = undefined;
        }
      }
      
      // Only set if we have a valid value, otherwise remove it
      if (validStateValue) {
        parsedData.stateProvince = validStateValue;
      } else {
        delete parsedData.stateProvince;
      }
    }

    // Set Smart Paste flag to prevent form reset useEffect from firing
    isSmartPastePopulating.current = true;
    
    // Populate all fields with shouldDirty: true to mark them as changed
    Object.entries(parsedData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        try {
          // Special handling for stateProvince - ensure it's valid
          if (key === 'stateProvince') {
            const stateOptions = getStates().map(s => s.value);
            if (!stateOptions.includes(value as string)) {
              console.warn(`⚠️ Skipping invalid state value: "${value}"`);
              return; // Skip setting invalid state values
            }
          }
          
          setValue(key as any, value, {
            shouldDirty: true,
            shouldValidate: true,
            shouldTouch: true,
          });
          console.log(`✅ Set ${key} = ${value}`);
        } catch (err) {
          console.error(`❌ Failed to set ${key}:`, err);
        }
      }
    });
    
    // Finalize after a small delay
    setTimeout(() => {
      trigger(); // Trigger validation
      setFormsDirty((prev: any) => ({
        ...prev,
        personalInformation: Object.keys(parsedData),
      }));
      
      // Check form state after population
      const formValues = getValues();
      const currentFormState = form.formState;
      console.log("✅ Smart Paste complete:", Object.keys(parsedData).length, "fields populated");
      console.log("📊 Form state after population:", {
        isValid: currentFormState.isValid,
        isDirty: currentFormState.isDirty,
        errors: currentFormState.errors,
        stateProvince: formValues.stateProvince,
      });
      
      // Clear Smart Paste flag
      setTimeout(() => {
        isSmartPastePopulating.current = false;
      }, 200);
    }, 100);

    toast({
      title: "✨ Personal Information Populated",
      description: `${Object.keys(parsedData).length} personal fields have been auto-filled. Please review and save before proceeding to financial information.`,
    });
  };
  
  // Assign handler to ref
  console.log('🔄 [Personal Tab] Assigning onPersonalDataParsed to ref');
  handlersRef.current.onPersonalDataParsed = handlePersonalDataParsed;

  // Handle Smart Paste - Financial Information (Step 2)
  const handleFinancialDataParsed = (parsedData: Partial<ClientType>) => {
    console.log("🤖 Smart Paste (Step 2 - Financial): Received financial data:", parsedData);
    
    // Store financial data for later
    setPendingFinancialData(parsedData);
    setShowFinancialPrompt(true);
    
    toast({
      title: "📊 Financial Information Ready",
      description: `${Object.keys(parsedData).length} financial fields are ready. Save personal information first, then click the prompt to continue.`,
      duration: 10000,
    });
  };
  
  // Assign handlers to ref
  console.log('🔄 [Personal Tab] Assigning onFinancialDataParsed to ref');
  handlersRef.current.onFinancialDataParsed = handleFinancialDataParsed;
  console.log('🔄 [Personal Tab] Assigning onSave to ref');
  handlersRef.current.onSave = async () => {
    console.log("💾 [Personal Tab] Smart Paste Save triggered");
    return handleSubmit(onSubmit)();
  };
  console.log('✅ [Personal Tab] All handler refs assigned');
  
  // Apply financial data when user is ready
  const applyFinancialData = () => {
    if (!pendingFinancialData || !handleTabChange) {
      console.warn('⚠️ Cannot apply financial data: missing data or tab change handler');
      return;
    }

    console.log("🔀 Switching to Financial Information tab and applying data");
    
    // Switch to financial tab
    handleTabChange("financial-information");
    
    // DON'T call onUpdate() - financial tab will handle the population
    setShowFinancialPrompt(false);
    setPendingFinancialData(null);
    
    // The financial tab's Smart Paste handler will be called separately
    toast({
      title: "🔀 Switched to Financial Tab",
      description: "Financial information will need to be populated separately using Smart Paste on the Financial tab.",
    });
  };

  return (
    <div className="py-6 mb-8">
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <PersonalInformationComponent client={client} />
          <FamilyInformationComponent client={client} />
          <RetirementInformationComponent client={client} />
          <SavingsInformationComponent client={client} />
          {crmEnabled && <ContactComponent client={client} />}

          <div className="fixed bottom-0 right-0 m-3 z-50 flex items-center gap-3">
            {getAutoSaveText() && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 rounded-md border border-gray-200">
                <Clock className="h-3.5 w-3.5 text-gray-500" />
                <span className="text-xs text-gray-600">
                  {getAutoSaveText()}
                </span>
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              className="p-3"
              onClick={() => setIsSmartPasteOpen(true)}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Smart Paste
            </Button>
            <Button
              type="submit"
              className="p-3 bg-accent"
              disabled={!isValid || !isDirty}
              onClick={(e) => {
                // Distinguish manual vs auto-save
                isAutoSaving.current = !e.isTrusted;
                console.log("💾 Save button clicked:", {
                  isValid,
                  isDirty,
                  canSave: isValid && isDirty,
                });
              }}
            >
              <Icons.save className="mr-2" />
              {isDirty ? "Save Changes" : "No Changes"}
            </Button>
          </div>
        </form>
      </FormProvider>

      {/* Smart Paste Modal is now rendered at parent level (edit-forms.tsx) */}

      {/* Financial Data Prompt Banner */}
      {showFinancialPrompt && pendingFinancialData && (
        <div className="fixed bottom-20 right-0 left-0 mx-auto max-w-2xl px-4 z-40">
          <Alert className="bg-green-50 border-2 border-accent shadow-lg">
            <Info className="h-5 w-5 text-accent" />
            <AlertDescription className="flex items-center justify-between">
              <div className="text-gray-900">
                <strong>Financial information is ready!</strong> Save personal details first, then click Continue to populate financial fields.
              </div>
              <div className="flex gap-2 ml-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowFinancialPrompt(false);
                    setPendingFinancialData(null);
                  }}
                >
                  Skip
                </Button>
                <Button
                  size="sm"
                  className="bg-accent"
                  onClick={applyFinancialData}
                >
                  Continue to Financial
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      )}
    </div>
  );
}
